/* eslint-disable @typescript-eslint/restrict-template-expressions */
/* eslint-disable @typescript-eslint/restrict-plus-operands */
/* eslint-disable @typescript-eslint/no-wrapper-object-types */

import { Inject, Injectable, Optional } from '@angular/core'
import { ApiServiceBase, IApiConfig } from '@sightline/models-misc'
import { HTTPMethod } from 'http-method-enum'
import {
  AccountInfoDto,
  IAccountFilterTable,
  RecentlyViewedAccount,
} from '../../../../src/app/accounts/account-search/models/accountTableList.model'
import {
  SaveUserDetailsRequest,
  SaveUserDetailsResponse,
} from '../../../../src/app/administration/user-details/models/save-user-details-request.model'
import { RoleValue, UserAccountState } from '../../../../src/app/administration/user-list/models/userTableCriteriaModel'
import { IUserFilterTable, IUserTable } from '../../../../src/app/administration/user-list/models/userTableList.model'
import { IEnterprise, IHierarchy, IUserHierarchy } from '../../../app/administration/user-list/models/hierarchy.model'
import {
  ITransactionFilterTable,
  TransactionInfoDto,
} from '../../../../src/app/transactions/transaction-search/models/transactionTableList.model'
import {
  DownloadedFileDto,
  IFilesTable,
  IReportFileFilterTable
} from '../../../../src/app/reports/download-files/models/reportFileTableList.model'
import { ReportConfigDto } from '../../../../src/app/home/models/report-config'
import { IOrgFilter, IOrgSearchResponse, Org, OrgInfo, OrgIpRestrictionModel, ProgramCardArt, ProgramCardPackageElligibility } from '../../../../src/commons/models/org.model'
import { Observable } from 'rxjs'
import { map } from 'rxjs/operators'
import { IReportFileSummary } from '../../../../src/app/reports/download-files/models/reportFileSummaryModel'
import { UserReport } from '../../../../src/commons/models/user-report.model'
import { ProgramGroup } from '../../../../src/commons/models/program-group.model'
import { IOrgHierarchyConfig, IUserHierarchyConfig } from '../../../../src/app/administration/user-hierarchy/models/user-Hierarchy.model'
import { OperatorConfigSettingDto } from '../../../../src/commons/models/configSettings.model'
import { Card } from '../../../../src/commons/models/card.model'
import { HttpEvent } from '@angular/common/http'
import { SearchOrgHistoryDto } from '../../../../src/app/administration/org-details/org-history/models/org-history'
import { UserHistoryViewModel } from '../../../app/administration/user-history/models/user-history.model'
import { SpanexConfig, SpanexConfigSearch } from '../../../../src/commons/models/spanex-config.mode'
import { AccountBalanceSearch, Balance } from '../../../../src/commons/models/account-balance.model'
import { PatronPreferenceDto } from '../../../../src/commons/models/patronPreference.model'
import { API_CONFIG } from '../../../../src/security/models/services/users/userApi.service'
import { ISetUserPermissions } from '../../../../src/security/models/user-invitation.model'
import { AppUserClaims, OrgPermissionDto, OrgRoleDto, UserDto } from '../../../../src/security/models'
import { CustomerWriteOffsDto, GetCustomerWriteOffsRequestDto } from 'src/app/funds-recovery/funds-recovery-details/models/customer-write-offs'
import { AllWriteOffsDto } from 'src/app/administration/negative-balance-summary/models/all-write-offs'
import { CheckRecoverFundsStatusRequestDto, CheckRecoverFundsStatusResponseDto, RecoverFundsRequestDto, RecoverFundsResponseDto } from 'src/app/funds-recovery/funds-recovery-details/models/recover-customer-write-offs'
import { UnblockCardRequestDto } from 'src/app/funds-recovery/funds-recovery-details/models/customer-card'

@Injectable()
export class ApiService {

  constructor(
    private serviceBase: ApiServiceBase,
    @Inject(API_CONFIG) @Optional() private config?: IApiConfig) { }

  // orgs
  public searchOrgs(payload: IOrgFilter): Observable<Org[]> {
    return this.serviceBase
      .callProtectedEndpoint<IOrgSearchResponse>(HTTPMethod.POST, 'org/search', this.config, payload)
      .pipe(map(q => q.data))
  }

  public async getCurrentOrgHierarchy(): Promise<IUserHierarchy> {
    return await this.serviceBase
      .callProtectedEndpoint<IUserHierarchy>(HTTPMethod.GET, 'users/org-hierarchy', this.config)
      .toPromise()
  }

  public async getProgramGroups(): Promise<ProgramGroup[]> {
    return await this.serviceBase
      .callProtectedEndpoint<ProgramGroup[]>(HTTPMethod.GET, 'business-config/program-groups', this.config)
      .toPromise()
  }

  public async getUsersDownloadReport(orgId: number, payload: IUserFilterTable): Promise<string> {
    return await this.serviceBase
      .callProtectedEndpoint<string>(HTTPMethod.POST, `users/${orgId}/download-report`, this.config, payload,
        {
          responseType: 'text' as 'json',
          observe: 'response'
        }).toPromise()
  }

  public async getOrgHistoryDownloadReport(orgId: number): Promise<string> {
    const result = await this.serviceBase
      .callProtectedEndpoint<Blob>(HTTPMethod.POST, `org/${orgId}/download-changes-history-report`, this.config, null,
        {
          responseType: 'Blob' as 'json',
          observe: 'response'
        }).toPromise()

    return result.text()
  }

  public async getUsersQuery(payload: IUserFilterTable): Promise<IUserTable> {
    return await this.serviceBase
      .callProtectedEndpoint<IUserTable>(HTTPMethod.POST, 'users/query', this.config, payload)
      .toPromise()
  }

  public getOrgsQuery(payload: IOrgFilter): Promise<IOrgSearchResponse> {
    return this.serviceBase
      .callProtectedEndpoint<IOrgSearchResponse>(HTTPMethod.POST, 'org/list', this.config, payload)
      .toPromise()
  }

  public async patchIpRestrictions(orgRId: number, payload: OrgIpRestrictionModel): Promise<boolean> {
    return await this.serviceBase
      .callProtectedEndpoint<boolean>(HTTPMethod.PATCH, `org/${orgRId}/ip-restriction`, this.config, payload)
      .toPromise()
  }

  public async getUserHierarchy(userId: number): Promise<IUserHierarchy> {
    return await this.serviceBase
      .callProtectedEndpoint<IUserHierarchy>(HTTPMethod.GET, `users/${userId}/hierarchy`, this.config)
      .toPromise()
  }

  public async getUserHistory(userId: number): Promise<UserHistoryViewModel[]> {
    return await this.serviceBase
      .callProtectedEndpoint<UserHistoryViewModel[]>(HTTPMethod.GET, `users/${userId}/history`, this.config)
      .toPromise()
  }

  public async getOrgHistory(orgId: number): Promise<SearchOrgHistoryDto> {
    return await this.serviceBase
      .callProtectedEndpoint<SearchOrgHistoryDto>(HTTPMethod.GET, `org/${orgId}/history-changes`, this.config)
      .toPromise()
  }

  public async getOrg(): Promise<Org> {
    return await this.serviceBase
      .callProtectedEndpoint<Org>(HTTPMethod.GET, 'org', this.config)
      .toPromise()
  }

  public async getOrgById(id: number): Promise<OrgInfo> {
    return await this.serviceBase
      .callProtectedEndpoint<OrgInfo>(HTTPMethod.GET, `org/${id}`, this.config)
      .toPromise()
  }

  public async upsertOrg(orgToAdd: Org): Promise<Org> {
    return await this.serviceBase
      .callProtectedEndpoint<Org>(HTTPMethod.POST, 'org', this.config, orgToAdd)
      .toPromise()
  }

  public async saveUserDetails(
    userId: number,
    saveUserRequest: SaveUserDetailsRequest
  ): Promise<SaveUserDetailsResponse> {
    return await this.serviceBase
      .callProtectedEndpoint<SaveUserDetailsResponse>(
        HTTPMethod.PATCH,
        `users/${userId}`,
        this.config,
        saveUserRequest
      )
      .toPromise()
  }

  public async saveUserPermissions(
    userId: number,
    saveUserPermissions: ISetUserPermissions
  ): Promise<void> {
    return await this.serviceBase
      .callProtectedEndpoint<void>(
        HTTPMethod.PUT,
        `users/${userId}/permissions`,
        this.config,
        saveUserPermissions
      )
      .toPromise()
  }

  public async saveUserHierarchy(
    userId: number,
    userHierarhcy: IUserHierarchyConfig
  ): Promise<void> {
    return await this.serviceBase
      .callProtectedEndpoint<void>(
        HTTPMethod.PUT,
        `users/${userId}/hierarchy`,
        this.config,
        userHierarhcy
      )
      .toPromise()
  }

  public async getRoles(): Promise<Array<RoleValue>> {
    return await this.serviceBase
      .callProtectedEndpoint<Array<RoleValue>>(HTTPMethod.GET, 'org/roles', this.config)
      .toPromise()
  }

  // end orgs

  // reports

  public async getUserReports(): Promise<Array<UserReport>> {
    return await this.serviceBase
      .callProtectedEndpoint<Array<UserReport>>(
        HTTPMethod.GET,
        'reports',
        this.config
      )
      .toPromise()
  }

  public async getReport(reportId: number, isDashboardReport = false): Promise<ReportConfigDto> {
    return await this.serviceBase
      .callProtectedEndpoint<ReportConfigDto>(
        HTTPMethod.GET,
        `reports/${reportId}?isDashboardReport=${isDashboardReport.toString()}`,
        this.config
      )
      .toPromise()
  }

  public async getUserReportDownloads(): Promise<Array<UserReport>> {
    return await this.serviceBase
      .callProtectedEndpoint<Array<UserReport>>(HTTPMethod.GET,
        'reports/downloads', this.config)
      .toPromise()
  }

  // reports

  // users

  public async getMyHierarchy(): Promise<IUserHierarchy> {
    return await this.serviceBase
      .callProtectedEndpoint<IUserHierarchy>(HTTPMethod.GET, 'users/hierarchy', this.config)
      .toPromise()
  }

  public async getMyProgramGroups(): Promise<Array<ProgramGroup>> {
    return await this.serviceBase
      .callProtectedEndpoint<Array<ProgramGroup>>(HTTPMethod.GET, 'users/program-groups', this.config)
      .toPromise()
  }

  public async isEmailUnique(email: string, userId: number): Promise<boolean> {
    return await this.serviceBase
      .callProtectedEndpoint<boolean>(HTTPMethod.POST, 'users/rpc-isEmailAvailable', this.config, {
        email: email,
        userId: userId,
      })
      .toPromise()
  }

  public async getAccountStates(): Promise<Array<UserAccountState>> {
    return await this.serviceBase
      .callProtectedEndpoint<Array<UserAccountState>>(HTTPMethod.GET, 'users/account-states', this.config)
      .toPromise()
  }

  // end users

  // accounts

  public async getAccountsQuery(payload: IAccountFilterTable): Promise<Array<AccountInfoDto>> {
    return await this.serviceBase
      .callProtectedEndpoint<Array<AccountInfoDto>>(HTTPMethod.POST, 'accounts/query', this.config, payload.filter)
      .toPromise()
  }

  public async getRecentlyViewedAccounts(): Promise<Array<RecentlyViewedAccount>> {
    return await this.serviceBase
      .callProtectedEndpoint<Array<RecentlyViewedAccount>>(
        HTTPMethod.GET,
        'accounts/recently-viewed-accounts',
        this.config
      )
      .toPromise()
  }

  public async postRecentlyViewedAccounts(accountDto: AccountInfoDto): Promise<void> {
    return await this.serviceBase
      .callProtectedEndpoint<void>(HTTPMethod.POST, 'accounts/recently-viewed-accounts', this.config, accountDto)
      .toPromise()
  }

  public async getTransactionsQuery(mmn: number, payload: ITransactionFilterTable): Promise<Array<TransactionInfoDto>> {
    return await this.serviceBase
      .callProtectedEndpoint<Array<TransactionInfoDto>>(
        HTTPMethod.POST,
        `accounts/${mmn}/transactions/query`,
        this.config,
        payload.filter
      )
      .toPromise()
  }

  public async getCardHistory(masterMemberNumber: string, programGroup: string): Promise<Array<AccountInfoDto>> {
    return await this.serviceBase
      .callProtectedEndpoint<Array<AccountInfoDto>>(HTTPMethod.POST, 'accounts/card-history', this.config, { masterMemberNumber: masterMemberNumber, programGroup: programGroup })
      .toPromise()
  }

  public async getCRBUser(programCode: string, masterMemberNumber: number): Promise<UserDto> {
    return await this.serviceBase
      .callProtectedEndpoint<UserDto>(
        HTTPMethod.GET,
        `accounts/crb/users/${programCode}/${masterMemberNumber}`,
        this.config
      )
      .toPromise()
  }

  public async getCards(cardReferenceNumber?: string): Promise<Array<Card>> {
    return await this.serviceBase
      .callProtectedEndpoint<Array<Card>>(HTTPMethod.GET, `accounts/crb/cards/${cardReferenceNumber}`, this.config)
      .toPromise()
  }

  public async getSpanexConfig(spanexConfigSearch: SpanexConfigSearch): Promise<SpanexConfig[]> {
    return await this.serviceBase
      .callProtectedEndpoint<SpanexConfig[]>(HTTPMethod.POST, 'accounts/spanex-config', this.config, spanexConfigSearch)
      .toPromise()
  }

  public async getBalance(accountBalanceSearch: AccountBalanceSearch): Promise<Balance> {
    return await this.serviceBase
      .callProtectedEndpoint<Balance>(HTTPMethod.POST, 'accounts/balance', this.config, accountBalanceSearch)
      .toPromise()
  }

  public async getCustomerWriteOffs(request: GetCustomerWriteOffsRequestDto): Promise<CustomerWriteOffsDto> {
    return await this.serviceBase
      .callProtectedEndpoint<CustomerWriteOffsDto>(HTTPMethod.POST, 'accounts/get-customer-write-offs', this.config, request)
      .toPromise()
  }

  public async getAllWriteOffs(): Promise<AllWriteOffsDto> {
    return await this.serviceBase
      .callProtectedEndpoint<AllWriteOffsDto>(HTTPMethod.GET, 'accounts/get-all-write-offs', this.config)
      .toPromise()
  }

  public async recoverCustomerWriteOffs(request: RecoverFundsRequestDto): Promise<RecoverFundsResponseDto> {
    return await this.serviceBase
      .callProtectedEndpoint<RecoverFundsResponseDto>(HTTPMethod.POST, 'accounts/recover-funds', this.config, request)
      .toPromise()
  }

  public async checkRecoverFundsStatus(request: CheckRecoverFundsStatusRequestDto): Promise<CheckRecoverFundsStatusResponseDto> {
    return await this.serviceBase
      .callProtectedEndpoint<CheckRecoverFundsStatusResponseDto>(HTTPMethod.POST, 'accounts/check-recover-funds-status', this.config, request)
      .toPromise()
  }

  public async unblockCustomerCard(request: UnblockCardRequestDto): Promise<boolean> {
    return await this.serviceBase
      .callProtectedEndpoint<boolean>(HTTPMethod.POST, 'accounts/unblock-customer-card', this.config, request)
      .toPromise()
  }


  // end accounts

  // files

  public async getReportFiles(
    payload: IReportFileFilterTable
  ): Promise<IFilesTable> {
    return await this.serviceBase
      .callProtectedEndpoint<IFilesTable>(HTTPMethod.POST, 'files/search', this.config, payload)
      .toPromise()
  }

  public async getFileSummary(fileId: number, reportType: string
  ): Promise<IReportFileSummary> {
    return await this.serviceBase
      .callProtectedEndpoint<IReportFileSummary>(HTTPMethod.GET, `files/${fileId}/summary?reportType=${reportType}`, this.config)
      .toPromise()
  }

  // end files

  //config setting

  public async getCommonConfigSettings(): Promise<OperatorConfigSettingDto[]> {
    return await this.serviceBase
      .callUnprotectedEndpoint<OperatorConfigSettingDto[]>(HTTPMethod.GET, 'config-settings/common', this.config)
      .toPromise()
  }

  public async getConfigSettings(configKeys: string[], orgRId?: number, programGroup?: string, programCode?: string): Promise<OperatorConfigSettingDto[]> {

    let url = 'config-settings?'
    url += configKeys.map(x => `configKeys=${x}`)

    if (orgRId)
      url += `&orgRId=${orgRId}`

    if (programGroup)
      url += `&programGroup=${programGroup}`

    if (programCode)
      url += `&programCode=${programCode}`

    return await this.serviceBase
      .callProtectedEndpoint<OperatorConfigSettingDto[]>(HTTPMethod.GET, url, this.config)
      .toPromise()
  }

  //end config setting

  public async logReportDownload(
    files: DownloadedFileDto[]
  ): Promise<IFilesTable> {
    return await this.serviceBase
      .callProtectedEndpoint<IFilesTable>(HTTPMethod.POST, 'files/recently-downloaded-files', this.config, { downloadedFiles: files })
      .toPromise()
  }

  public async getOrgPermissions(orgId: number): Promise<OrgPermissionDto> {
    return await this.serviceBase
      .callProtectedEndpoint<OrgPermissionDto>(HTTPMethod.GET, `org/${orgId}/permissions`, this.config)
      .toPromise()
  }

  public async getOrgClaims(orgId: number, includeAllClaims: boolean): Promise<AppUserClaims[]> {
    return await this.serviceBase
      .callProtectedEndpoint<AppUserClaims[]>(HTTPMethod.GET, `org/${orgId}/claims?includeNotSavedClaims=${includeAllClaims}`, this.config)
      .toPromise()
  }

  public async upsertOrgRoles(orgId: number, role: OrgRoleDto[]): Promise<boolean> {
    return await this.serviceBase
      .callProtectedEndpoint<boolean>(HTTPMethod.POST, `org/${orgId}/roles`, this.config, role)
      .toPromise()
  }

  public async getOrgCardArts(orgId: number): Promise<ProgramCardArt[]> {
    return await this.serviceBase
      .callProtectedEndpoint<ProgramCardArt[]>(HTTPMethod.GET, `card-arts/${orgId}/get-programgroup-card-arts`, this.config)
      .toPromise()
  }

  public async getProgramCardArts(programGroup: string): Promise<ProgramCardArt[]> {
    return await this.serviceBase
      .callProtectedEndpoint<ProgramCardArt[]>(HTTPMethod.GET, `card-arts/get-programgroup-card-arts/${programGroup}`, this.config)
      .toPromise()
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public uploadCardArt(programGroup: string, formData: any): Observable<HttpEvent<Object>> {
    return this.serviceBase
      .callProtectedEndpoint<HttpEvent<Object>>(HTTPMethod.POST, `card-arts/upload-card-art-file/${programGroup}`, this.config, formData, { reportProgress: true, observe: 'events' })
  }

  public async updateCardArt(cardArts: ProgramCardArt[]): Promise<boolean> {
    return this.serviceBase
      .callProtectedEndpoint<boolean>(HTTPMethod.PATCH, 'card-arts/update-programgroup-card-arts', this.config, cardArts)
      .toPromise()
  }

  public async searchEnterprise(stakeholderId: string): Promise<IEnterprise[]> {
    return await this.serviceBase
      .callProtectedEndpoint<IEnterprise[]>(HTTPMethod.GET, `business-config/enterprise-params?StakeHolderIds=${stakeholderId}`, this.config)
      .toPromise()
  }

  public async getOrgHierarchy(orgId: number): Promise<IHierarchy> {
    return await this.serviceBase
      .callProtectedEndpoint<IHierarchy>(HTTPMethod.GET, `org/${orgId}/hierarchy`, this.config)
      .toPromise()
  }

  public async updateOrgHierarchy(orgId: number, hierarchyConfig: IOrgHierarchyConfig): Promise<void> {
    await this.serviceBase
      .callProtectedEndpoint<IHierarchy>(HTTPMethod.PUT, `org/${orgId}/hierarchy`, this.config, hierarchyConfig)
      .toPromise()
  }

  public async updateOrgClaims(orgId: number, claimsSelected: AppUserClaims[]): Promise<void> {
    await this.serviceBase
      .callProtectedEndpoint<boolean>(HTTPMethod.PATCH, `org/${orgId}/claims`, this.config, { claims: claimsSelected })
      .toPromise()
  }

  public async getPatronPreferences(cardReferenceNumber: string): Promise<Array<PatronPreferenceDto>> {
    return await this.serviceBase
      .callProtectedEndpoint<Array<PatronPreferenceDto>>(
        HTTPMethod.GET,
        `accounts/${cardReferenceNumber}/patron-preferences`,
        this.config
      )
      .toPromise()
  }

  public async updatetPatronPreference(cardReferenceNumber: string, patronPreference: PatronPreferenceDto): Promise<void> {
    await this.serviceBase
      .callProtectedEndpoint<Array<PatronPreferenceDto>>(
        HTTPMethod.POST,
        `accounts/${cardReferenceNumber}/patron-preferences`,
        this.config,
        patronPreference
      )
      .toPromise()
  }

  public async orderCard(cardReferenceNumber: string): Promise<void> {
    await this.serviceBase
      .callProtectedEndpoint<Array<PatronPreferenceDto>>(
        HTTPMethod.POST,
        `accounts/${cardReferenceNumber}/order-card`,
        this.config
      )
      .toPromise()
  }

  public async getProgramCardPackages(programCode: string, memberNumber: string): Promise<ProgramCardPackageElligibility> {
    return await this.serviceBase
      .callProtectedEndpoint<ProgramCardPackageElligibility>(HTTPMethod.GET, `card-packages/${programCode}?memberNumber=${memberNumber}`, this.config)
      .toPromise()
  }

  public async updateCardPackage(programCode: string, cardPackageId: string, memberNumber: string): Promise<void> {
    await this.serviceBase
      .callProtectedEndpoint<Array<PatronPreferenceDto>>(
        HTTPMethod.POST,
        `card-packages/${programCode}/${cardPackageId}`,
        this.config,
        {
          memberNumber: memberNumber
        }
      )
      .toPromise()
  }
}
